import { createSelector } from 'reselect'
import { chain } from 'immutadot'
import { getMediaJumpStep } from '../configuration/selectors'
import { isCollaborativeIndexationCreationOpened } from '../collaborativeIndexations/selectors'
import { isPrintPreviewOpened } from '../print/selectors'

export const getNavigation = state => state.navigation

export const getMedia = createSelector(
    getNavigation,
    navigation => navigation && navigation.media
)

export const isNavigationEnabled = createSelector(
    isPrintPreviewOpened,
    isCollaborativeIndexationCreationOpened,
    (isPrintPreviewOpened, isCollaborativeIndexationCreationOpened) => ! isPrintPreviewOpened && ! isCollaborativeIndexationCreationOpened
)

export const getGroups = createSelector(
    getNavigation,
    navigation => navigation && navigation.groups
)

export const getMediaPosition = createSelector(
  getMedia,
  media => media && media.position,
)

export const getMediaCount = createSelector(
    getMedia,
    media => media && media.count,
)

export const initializeSelector = createSelector(
    context => context,
    initialize,
)

export const jumpToMediaSelector = createSelector(
    getNavigation,
    (state, position) => position,
    jumpTo,
)

export const forwardMediaSelector = createSelector(
    getNavigation,
    getMediaPosition,
    getMediaJumpStep,
    (navigation, mediaPosition, mediaJumpStep) => jumpTo(navigation, mediaPosition + mediaJumpStep)
)

export const backwardMediaSelector = createSelector(
    getNavigation,
    getMediaPosition,
    getMediaJumpStep,
    (navigation, mediaPosition, mediaJumpStep) => jumpTo(navigation, mediaPosition - mediaJumpStep),
)

export const previousMediaSelector = createSelector(
    getNavigation,
    navigation => jumpTo(navigation, navigation.media.position - 1),
)

export const nextMediaSelector = createSelector(
    getNavigation,
    navigation => jumpTo(navigation, navigation.media.position + 1),
)

export const firstMediaSelector = createSelector(
    getNavigation,
    navigation => jumpTo(navigation, 0)
)

export const lastMediaSelector = createSelector(
    getNavigation,
    getMediaCount,
    (navigation, count) => jumpTo(navigation, count - 1)
)

export const getGroupPosition = createSelector(
    getGroups,
    groups => groups && groups.position,
)

export const previousGroupPositionSelector = createSelector(
    getNavigation,
    navigation => jumpToGroup(navigation, navigation.groups.position - 1)
)

export const nextGroupPositionSelector = createSelector(
    getNavigation,
    navigation => jumpToGroup(navigation, navigation.groups.position + 1)
)

export const getGroupsCount = createSelector(
    getGroups,
    groups => groups && groups.count,
)

export const jumpToGroupPositionSelector = createSelector(
    getGroupsCount,
    (state, position) => position,
    (count, position) => Math.max(Math.min(position, count - 1), 0)
)

export const getPrevNextPropsAction = props => props.action

export const isPreviousAction = createSelector(
    getPrevNextPropsAction,
    (action) => action === 'prev'
)

export const isNextAction = createSelector(
    getPrevNextPropsAction,
    (action) => action === 'next'
)

export const hasPreviousMedia = createSelector(
    getMedia,
    media => media && media.hasPrevious,
)

export const hasNextMedia = createSelector(
    getMedia,
    media => media && media.hasNext,
)

export const hasPreviousGroup = createSelector(
    getGroups,
    groups => groups && groups.hasPrevious,
)

export const hasNextGroup = createSelector(
    getGroups,
    groups => groups && groups.hasNext,
)

export const isPrevMediaNavEnabled = createSelector(
    hasPreviousMedia,
    isNavigationEnabled,
    (hasPreviousMedia, isNavigationEnabled) => hasPreviousMedia && isNavigationEnabled
)

export const isNextMediaNavEnabled = createSelector(
    hasNextMedia,
    isNavigationEnabled,
    (hasNextMedia, isNavigationEnabled) => hasNextMedia && isNavigationEnabled
)

export const isPrevGroupNavEnabled = createSelector(
    hasPreviousGroup,
    isNavigationEnabled,
    (hasPreviousGroup, isNavigationEnabled) => hasPreviousGroup && isNavigationEnabled
)

export const isNextGroupNavEnabled = createSelector(
    hasNextGroup,
    isNavigationEnabled,
    (hasNextGroup, isNavigationEnabled) => hasNextGroup && isNavigationEnabled
)

export const keepMediaPositionInBoundaries = createSelector(
    getMediaCount,
    (state, position) => position,
    (mediaCount, position) => Math.max(Math.min(position, mediaCount - 1), 0)
)

export const isFastNavigationEnabled = createSelector(
    getMediaCount,
    getMediaJumpStep,
    (count, mediaJumpStep) => count > mediaJumpStep,
)

function initialize(navigation)
{
    const { counts, positions } = navigation

    navigation = {
        groups: {
            count: counts.group,
            position: positions.group,
        },
        media: {
            count: counts.media,
            position: positions.media,
        }
    }

    return chain(navigation)
        .set('media.hasPrevious', isNewPositionValid(positions.media - 1, counts.media))
        .set('media.hasNext', isNewPositionValid(positions.media + 1, counts.media))
        .set('groups.hasPrevious', isNewPositionValid(positions.group - 1, counts.group))
        .set('groups.hasNext', isNewPositionValid(positions.group + 1, counts.group))
        .value()
}

function jumpTo(navigation, position)
{
    if(position < 0)
    {
        position = 0
    }

    if(position >= navigation.media.count)
    {
        position = navigation.media.count -1
    }

    return chain(navigation)
        .set('media.hasPrevious', isNewPositionValid(position - 1, navigation.media.count))
        .set('media.hasNext', isNewPositionValid(position + 1, navigation.media.count))
        .set('media.position', position)
        .value()
}

function jumpToGroup(navigation, newPosition)
{
    return isNewPositionValid(newPosition, navigation.groups.count) ? newPosition : navigation.groups.position
}

function isNewPositionValid(position, mediaCount)
{
   if(position < 0 || position >= mediaCount)
   {
       return false
   }

   return true
}
